import React from "react";
import { View, Text, TouchableOpacity, StyleSheet, Linking, FlatList } from "react-native";
import STRINGS from "../constants/strings";
import { COLORS } from "../theme/colors";
/**
 * Opens a link based on the provided URL string.
 *
 * @param {string} url - The URL string to determine which link to open.
 * @returns {void}
 *
 * The function handles the following cases:
 * - `STRINGS.submitFeedback`: Opens the default mail client with a pre-filled email.
 * - `STRINGS.visitAppStore`: Opens the respective app store link based on the platform (iOS or Android).
 * - `STRINGS.downloadOtherApps`: Opens a link to download other apps.
 *
 * If the URL does not match any of the predefined cases, the function does nothing.
 */
const openLink = async (url) => {
  try {
    switch (url) {
      case STRINGS.submitFeedback:
        await Linking.openURL(STRINGS.seamlessLink);
        break;

      case STRINGS.downloadOtherApps:
        await Linking.openURL(STRINGS.vbconnectOtherAppsLink);
        break;

      default:
        console.warn("Invalid URL passed to openLink");
        break;
    }
  } catch (error) {
    console.error("Error opening link:", error);
  }
};

const handleSubmitFeedback = () => openLink(STRINGS.submitFeedback);
const handleVisitAppStore = () => openLink(STRINGS.visitAppStore);
const handleDownloadOtherApps = () => openLink(STRINGS.downloadOtherApps);

// Don't show Build and Env in production
const isProd = process.env.REACT_APP_ENV === "prod";
const versionString = isProd
  ? `${STRINGS.version} ${process.env.REACT_APP_VERSION}`
  : `${STRINGS.version} ${process.env.REACT_APP_VERSION} (${process.env.REACT_APP_BUILD}) ${process.env.REACT_APP_ENV}`;

const settingsData = [
  {
    id: "1",
    title: STRINGS.about,
    content: versionString,
  },
  {
    id: "2",
    title: STRINGS.moreInformation,
    content: [{ label: STRINGS.downloadOtherApps, action: handleDownloadOtherApps }],
  },
];

const Settings = () => {
  return (
    <FlatList
      data={settingsData}
      keyExtractor={(item) => item.id}
      contentContainerStyle={styles.contentContainer}
      renderItem={({ item }) => (
        <View style={styles.card}>
          <Text accessible={true} accessibilityRole="header" accessibilityLabel={item.title} style={styles.title}>
            {item.title}
          </Text>
          {Array.isArray(item.content) ? (
            item.content.map((button, index) => (
              <TouchableOpacity
                key={index}
                onPress={button.action}
                accessible={true}
                accessibilityRole="link"
                accessibilityLabel={button.label}
              >
                <Text
                  style={[styles.subTitle, styles.subTitleButton]}
                  accessible={true}
                  accessibilityRole="link"
                  accessibilityLabel={button.label}
                >
                  {button.label}
                </Text>
              </TouchableOpacity>
            ))
          ) : (
            <Text accessible={true} accessibilityRole="text" style={styles.textSecondary}>
              {item.content}
            </Text>
          )}
        </View>
      )}
      ListFooterComponent={<View style={{ height: 120 }} />}
    />
  );
};

const styles = StyleSheet.create({
  contentContainer: {
    padding: 16,
  },
  textSecondary: {
    color: COLORS.textSecondary,
  },
  card: {
    alignItems: "flex-start",
    padding: 20,
    backgroundColor: COLORS.textLight,
    borderRadius: 8,
    marginBottom: 16,
    borderColor: COLORS.border,
    borderWidth: 0.5,
  },
  title: {
    fontSize: 18,
    fontWeight: "700",
    color: COLORS.textDarker,
    lineHeight: 28,
  },
  subTitle: {
    fontSize: 14,
    marginTop: 4,
    lineHeight: 24,
    color: COLORS.border,
  },
  subTitleButton: {
    color: COLORS.greenLink,
    textDecorationLine: "underline",
  },
});

export default Settings;
